import { Heart } from "lucide-react";
import { type QuizConfig } from "~/types/quiz";

export const attachmentStyle: QuizConfig = {
  id: "stil-vezanosti",
  illustration: {
    src: "/images/quizzes/stil-vezanosti.webp",
    alt: "Dve osobe na klupi pokušavaju da se približe jedna drugoj.",
  },
  lastModified: "2026-07-31",
  title: "Test stila afektivne vezanosti",
  shortTitle: "Stil afektivne vezanosti",
  description:
    "Otkrij svoj stil afektivne vezanosti u odnosima - anksiozni, siguran ili izbegavajući.",
  longDescription: `Stil afektivne vezanosti definiše kako se ponašaš u intimnim odnosima i utiče na to kako se povezuješ sa partnerom, kako komuniciraš potrebe i kako se nosiš sa konfliktima. Postoje tri glavna stila: anksiozni (strah od odbacivanja), siguran (udobnost sa bliskošću i nezavisnošću) i izbegavajući (distanca u odnosima).`,
  duration: "5-10 min",
  icon: Heart,
  category: "Odnosi i komunikacija",
  focusAreasWanted: [
    "relationships",
    "couples_issues",
    "anxiety",
    "emotional_regulation",
  ],
  promptExtension: `
    - Present the primary attachment style prominently
    - Show all three attachment styles with their percentages
    - Use the bucket interpretations (full/partial/no) to provide nuanced feedback
    - Explain what each attachment style means in practical terms for relationships
    - Provide actionable advice for improving relationship patterns based on results
    - Consider showing ranked results if there are multiple high-scoring styles
  `,
  questions: [
    // Scoring: 0 = A (anxious), 1 = B (secure), 2 = C (avoidant)
    {
      type: "radio",
      question: "Često brinem da će moj partner prestati da me voli.",
      options: ["Da", "Ne"],
      scores: [0, 1], // yes: A, no: B
    },
    {
      type: "radio",
      question: "Lako mi je da pokažem naklonost svom partneru.",
      options: ["Da", "Ne"],
      scores: [1, 2], // yes: B, no: C
    },
    {
      type: "radio",
      question:
        "Plašim se da kada me neko bolje upozna, neće mu se svideti kakav/kakva zaista jesam.",
      options: ["Da", "Ne"],
      scores: [0, 1], // yes: A, no: B
    },
    {
      type: "radio",
      question:
        "Brzo se oporavljam nakon raskida. Čudno je kako lako mogu da nekoga izbacim iz misli.",
      options: ["Da", "Ne"],
      scores: [2, 0], // yes: C, no: A
    },
    {
      type: "radio",
      question: "Kada nisam u vezi, osećam se anksiozno i nepotpuno.",
      options: ["Da", "Ne"],
      scores: [0, 1], // yes: A, no: B
    },
    {
      type: "radio",
      question:
        "Teško mi je da emocionalno podržim partnera kada mu/joj je teško.",
      options: ["Da", "Ne"],
      scores: [2, 1], // yes: C, no: B
    },
    {
      type: "radio",
      question:
        "Kada je moj partner odsutan, plašim se da bi mogao/mogla da se zainteresuje za nekog drugog.",
      options: ["Da", "Ne"],
      scores: [0, 1], // yes: A, no: B
    },
    {
      type: "radio",
      question: "Prijatno mi je da zavišem od romantičnih partnera.",
      options: ["Da", "Ne"],
      scores: [1, 2], // yes: B, no: C
    },
    {
      type: "radio",
      question: "Moja nezavisnost mi je važnija od mojih odnosa.",
      options: ["Da", "Ne"],
      scores: [2, 1], // yes: C, no: B
    },
    {
      type: "radio",
      question: "Radije ne delim svoja najdublja osećanja sa partnerom.",
      options: ["Da", "Ne"],
      scores: [2, 1], // yes: C, no: B
    },
    {
      type: "radio",
      question:
        "Kada pokažem partneru kako se osećam, plašim se da on/ona neće osećati isto prema meni.",
      options: ["Da", "Ne"],
      scores: [0, 1], // yes: A, no: B
    },
    {
      type: "radio",
      question: "Generalno sam zadovoljan/zadovoljna svojim vezama.",
      options: ["Da", "Ne"],
      scores: [1, 0], // yes: B, no: A
    },
    {
      type: "radio",
      question: "Ne osećam potrebu da dramatizujem u svojim vezama.",
      options: ["Da", "Ne"],
      scores: [1, 0], // yes: B, no: A
    },
    {
      type: "radio",
      question: "Mnogo razmišljam o svojim odnosima.",
      options: ["Da", "Ne"],
      scores: [0, 1], // yes: A, (no has no explicit scoring, defaults to opposite)
    },
    {
      type: "radio",
      question: "Teško mi je da zavisim od romantičnih partnera.",
      options: ["Da", "Ne"],
      scores: [2, 1], // yes: C, no: B
    },
    {
      type: "radio",
      question: "Obično se vrlo brzo emociono vežem za partnera.",
      options: ["Da", "Ne"],
      scores: [0, 1], // yes: A
    },
    {
      type: "radio",
      question:
        "Nemam poteškoća sa izražavanjem svojih potreba i želja partneru.",
      options: ["Da", "Ne"],
      scores: [1, 2], // yes: B, no: C
    },
    {
      type: "radio",
      question:
        "Ponekad se osećam ljuto ili nervozno sa partnerom, a da ne znam zašto.",
      options: ["Da", "Ne"],
      scores: [0, 1], // yes: A, no: B
    },
    {
      type: "radio",
      question: "Vrlo sam osetljiv/osetljiva na raspoloženje svog partnera.",
      options: ["Da", "Ne"],
      scores: [0, 1], // yes: A, no: B
    },
    {
      type: "radio",
      question: "Verujem da je većina ljudi u osnovi poštena i pouzdana.",
      options: ["Da", "Ne"],
      scores: [1, 2], // yes: B, no: C
    },
    {
      type: "radio",
      question:
        "Više volim ležerni seks bez obaveza nego intiman seks sa jednom osobom.",
      options: ["Da", "Ne"],
      scores: [2, 1], // yes: C, no: B
    },
    {
      type: "radio",
      question:
        "Prijatno mi je da delim svoje lične misli i osećanja sa partnerom.",
      options: ["Da", "Ne"],
      scores: [1, 2], // yes: B, no: C
    },
    {
      type: "radio",
      question:
        "Brinem da ako me partner ostavi, možda nikada neću naći nekog drugog.",
      options: ["Da", "Ne"],
      scores: [0, 1], // yes: A, no: B
    },
    {
      type: "radio",
      question: "Nervira me kada mi partner previše priđe.",
      options: ["Da", "Ne"],
      scores: [2, 1], // yes: C, no: B
    },
    {
      type: "radio",
      question:
        "Tokom konflikta, sklona/sklon sam da impulsivno radim ili govorim stvari koje kasnije požalim, umesto da rasuđujem.",
      options: ["Da", "Ne"],
      scores: [0, 1], // yes: A, no: B
    },
    {
      type: "radio",
      question:
        "Svađa sa partnerom obično ne navodi me da doveden u pitanje celu našu vezu.",
      options: ["Da", "Ne"],
      scores: [1, 0], // yes: B, no: A
    },
    {
      type: "radio",
      question:
        "Moji partneri često žele da budem intimniji/intimnija nego što mi je prijatno.",
      options: ["Da", "Ne"],
      scores: [2, 1], // yes: C, no: B
    },
    {
      type: "radio",
      question: "Brinem da nisam dovoljno dobar/dobra.",
      options: ["Da", "Ne"],
      scores: [0, 1], // yes: A, no: B
    },
    {
      type: "radio",
      question:
        "Ponekad ljudi kažu da sam dosadan/dosadna jer stvaram malo drame u odnosima.",
      options: ["Da", "Ne"],
      scores: [1, 0], // yes: B, no: A
    },
    {
      type: "radio",
      question:
        "Nedostaje mi partner kada smo odvojeni, ali kada smo zajedno osećam potrebu da pobegnem.",
      options: ["Da", "Ne"],
      scores: [2, 1], // yes: C, no: B
    },
    {
      type: "radio",
      question:
        "Kada se ne slažem sa nekim, prijatno mi je da izrazim svoje mišljenje.",
      options: ["Da", "Ne"],
      scores: [1, 0], // yes: B, no: A
    },
    {
      type: "radio",
      question: "Mrzim osećaj da drugi ljudi zavise od mene.",
      options: ["Da", "Ne"],
      scores: [2, 1], // yes: C, no: B
    },
    {
      type: "radio",
      question:
        "Ako primetim da osoba koja me zanima gleda druge ljude, možda će me to uznemiravati. Mogu osetiti trunku ljubomore, ali je prolazna.",
      options: ["Da", "Ne"],
      scores: [1, 0], // yes: B, no: A
    },
    {
      type: "radio",
      question:
        "Ako primetim da osoba koja me zanima gleda druge ljude, osećam olakšanje – znači da ne želi da stvari budu ekskluzivne.",
      options: ["Da", "Ne"],
      scores: [2, 1], // yes: C, no: B
    },
    {
      type: "radio",
      question:
        "Ako primetim da osoba koja me zanima gleda druge ljude, to me čini depresivnim/depresivnom.",
      options: ["Da", "Ne"],
      scores: [0, 1], // yes: A, no: B
    },
    {
      type: "radio",
      question:
        "Ako osoba sa kojom izlazim počne da se ponaša hladno i distancirano, možda ću se zapitati šta se desilo, ali znaću da verovatno nije zbog mene.",
      options: ["Da", "Ne"],
      scores: [1, 0], // yes: B, no: A
    },
    {
      type: "radio",
      question:
        "Ako osoba sa kojom izlazim počne da se ponaša hladno i distancirano, verovatno ću biti ravnodušan/ravnodušna; možda čak i olakšan/olakšana.",
      options: ["Da", "Ne"],
      scores: [2, 0], // yes: C, no: A
    },
    {
      type: "radio",
      question:
        "Ako osoba sa kojom izlazim počne da se ponaša hladno i distancirano, brineću da sam učinio/učinila nešto pogrešno.",
      options: ["Da", "Ne"],
      scores: [0, 1], // yes: A, no: B
    },
    {
      type: "radio",
      question:
        "Ako bi moj partner prekinuo sa mnom, pokušao/pokušala bih da mu/joj pokažem šta propušta (malo ljubomore ne može škoditi).",
      options: ["Da", "Ne"],
      scores: [0, 1], // yes: A, no: B
    },
    {
      type: "radio",
      question:
        "Ako bi osoba sa kojom izlazim nekoliko meseci rekla da želi da prestane da se viđamo, bio/bila bih povređen/povređena u početku, ali bih to prebrodio/prebrodila.",
      options: ["Da", "Ne"],
      scores: [1, 0], // yes: B, no: A
    },
    {
      type: "radio",
      question:
        "Ponekad kada dobijem ono što želim u vezi, nisam siguran/sigurna šta još želim.",
      options: ["Da", "Ne"],
      scores: [2, 1], // yes: C, no: B
    },
    {
      type: "radio",
      question:
        "Neću imati problema da ostanem u kontaktu sa bivšim partnerom (strogo platonski) – na kraju krajeva, imamo mnogo zajedničkog.",
      options: ["Da", "Ne"],
      scores: [2, 1], // yes: C, no: B
    },
    {
      type: "textarea",
      question: "Opiši kako se tvoj stil vezanosti odražava u tvojim vezama:",
    },
  ],
  copy: {
    title: "Tvoj stil afektivne vezanosti",
    subtitle: "Otkrij kako se ponašaš u intimnim odnosima",
    scoreLabel: "Tvoji rezultati po stilovima afektivne vezanosti:",
    adviceTitle: "Tvoj profil afektivne vezanosti",
    resourcesTitle: "Preporučujemo da pročitaš:",
    whatNextTitle: "Šta dalje?",
    shareButtonText: "Podeli rezultat",
  },
};
